import { h, Component } from "preact";
import { observer } from "mobx-react";

import { Select, withStore } from "@searchspring/snap-preact-components";

@withStore
@observer
export class SortBy extends Component {
  constructor(props) {
    super(props);
    this.sortByRef = null;
    this.mutationObserver = null;
    this.focusableElements = [];
    this.buttonElement = null;
    this.labelElement = null;
    this.dropdownElement = null;
    this.isOpen = false;

    this.handleKeyDown = this.handleKeyDown.bind(this);
    this.handleFocusIn = this.handleFocusIn.bind(this);
    this.setupFocusTrap = this.setupFocusTrap.bind(this);
    this.teardownFocusTrap = this.teardownFocusTrap.bind(this);
    this.observeDropdown = this.observeDropdown.bind(this);
  }

  getFocusableElements() {
    if (!this.sortByRef) return [];
    
    const dropdown = this.sortByRef.querySelector('.ss__dropdown--open');
    if (!dropdown) return [];

    const select = dropdown.querySelector('.ss__select__select');
    if (!select) return [];

    return Array.from(select.querySelectorAll('.ss__select__select__option'))
      .filter(el => !el.disabled && el.offsetParent !== null);
  }

  focusLabel() {
    const labelElement = this.labelElement || this.sortByRef?.querySelector('.ss__select__label');
    if (!labelElement) return;
    
    if (!labelElement.hasAttribute('tabindex')) {
      labelElement.setAttribute('tabindex', '-1');
    }
    labelElement.focus();
  }

  setupFocusTrap() {
    this.focusableElements = this.getFocusableElements();
    
    if (this.focusableElements.length === 0) return;

    const dropdown = this.sortByRef?.querySelector('.ss__dropdown--open');
    if (!dropdown) return;

    this.dropdownElement = dropdown;
    this.buttonElement = this.sortByRef?.querySelector('.ss__select__dropdown__button');
    this.labelElement = this.sortByRef?.querySelector('.ss__select__label');

    const [firstElement] = this.focusableElements;
    firstElement?.focus();

    dropdown.addEventListener('keydown', this.handleKeyDown, true);
    for (const element of this.focusableElements) {
      element.addEventListener('keydown', this.handleKeyDown, true);
    }

    document.addEventListener('focusin', this.handleFocusIn);
  }

  teardownFocusTrap() {
    if (this.dropdownElement) {
      this.dropdownElement.removeEventListener('keydown', this.handleKeyDown, true);
    }

    for (const element of this.focusableElements) {
      element.removeEventListener('keydown', this.handleKeyDown, true);
    }

    document.removeEventListener('focusin', this.handleFocusIn);
    
    requestAnimationFrame(() => this.focusLabel());
  }

  handleKeyDown(e) {
    if (!this.isOpen) return;

    if (e.key === 'Escape') {
      e.preventDefault();
      e.stopPropagation();
      this.closeDropdown();
      return;
    }

    if (e.key === 'Enter') {
      const currentFocus = document.activeElement;
      if (this.focusableElements.includes(currentFocus)) {
        e.preventDefault();
        e.stopPropagation();
        currentFocus.click();
      }
      return;
    }

    if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
      if (this.focusableElements.length === 0) return;

      e.preventDefault();
      e.stopPropagation();

      const currentFocus = document.activeElement;
      const currentIndex = this.focusableElements.indexOf(currentFocus);
      const isArrowDown = e.key === 'ArrowDown';

      let nextElement;
      if (currentIndex === -1) {
        const [first] = this.focusableElements;
        const last = this.focusableElements.slice().reverse().find(() => true);
        nextElement = isArrowDown ? first : last;
      } else {
        const elementsAfter = this.focusableElements.slice(currentIndex + 1);
        const elementsBefore = this.focusableElements.slice(0, currentIndex);
        const [first] = this.focusableElements;
        const last = this.focusableElements.slice().reverse().find(() => true);

        if (isArrowDown) {
          nextElement = elementsAfter.find(() => true) || first;
        } else {
          nextElement = elementsBefore.slice().reverse().find(() => true) || last;
        }
      }

      nextElement?.focus();
      return;
    }

    if (e.key !== 'Tab' || this.focusableElements.length === 0) return;

    const [first] = this.focusableElements;
    const last = this.focusableElements.slice().reverse().find(() => true);
    const currentFocus = document.activeElement;

    if (e.shiftKey && currentFocus === first) {
      e.preventDefault();
      last?.focus();
    } else if (!e.shiftKey && currentFocus === last) {
      e.preventDefault();
      first?.focus();
    }
  }

  handleFocusIn(e) {
    if (!this.isOpen || !this.sortByRef) return;

    const dropdown = this.sortByRef.querySelector('.ss__dropdown--open');
    if (!dropdown) {
      this.closeDropdown();
      return;
    }

    const { target } = e;
    const isInsideDropdown = dropdown.contains(target);
    const isButton = this.buttonElement === target;
    const isLabel = this.labelElement === target;

    if (!isInsideDropdown && !isButton && !isLabel) {
      this.closeDropdown();
    }
  }

  closeDropdown() {
    const button = this.buttonElement || this.sortByRef?.querySelector('.ss__select__dropdown__button');
    if (!button) return;
    
    button.click();
    requestAnimationFrame(() => this.focusLabel());
  }

  observeDropdown() {
    if (!this.sortByRef) return;

    const dropdown = this.sortByRef.querySelector('.ss__dropdown');
    if (!dropdown) return;

    this.dropdownElement = dropdown;

    this.mutationObserver = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (mutation.type === 'attributes' && mutation.attributeName === 'class') {
          const isOpen = dropdown.classList.contains('ss__dropdown--open');
          
          if (isOpen && !this.isOpen) {
            this.isOpen = true;
            requestAnimationFrame(() => this.setupFocusTrap());
          } else if (!isOpen && this.isOpen) {
            this.isOpen = false;
            this.teardownFocusTrap();
          }
        }
      }
    });

    this.mutationObserver.observe(dropdown, {
      attributes: true,
      attributeFilter: ['class']
    });

    this.isOpen = dropdown.classList.contains('ss__dropdown--open');
    if (this.isOpen) {
      requestAnimationFrame(() => this.setupFocusTrap());
    }
  }

  componentDidMount() {
    requestAnimationFrame(() => {
      if (this.sortByRef) {
        this.observeDropdown();
      }
    });
  }

  componentDidUpdate() {
    if (this.sortByRef && !this.mutationObserver) {
      requestAnimationFrame(() => {
        this.observeDropdown();
      });
    }
  }

  componentWillUnmount() {
    if (this.mutationObserver) {
      this.mutationObserver.disconnect();
    }
    this.teardownFocusTrap();
  }

  render() {
    const { sorting } = this.props.store;

    return (
      <div ref={(el) => { this.sortByRef = el; }}>
        <Select
          className="ss-sort-by"
          label={window.Resources.searchspring.toolbar.sort}
          options={sorting.options}
          selected={sorting.current}
          onSelect={(e, selection) => {
            selection.url.go();
          }}
        />
      </div>
    );
  }
}
